// packages/game-shell/src/sim/fixtures/dsl/FixtureActions.ts

import { CanonicalActionTypeNames } from "@slot/client-engine";
import type { ActionPhaseName } from "@slot/client-engine";

/**
 * FixtureActions (DSL helpers)
 *
 * Goal:
 * - Provide ONE canonical constructor per canonical action type
 * - Use CanonicalActionTypeNames so strings never drift
 * - Default phases match client-engine ActionPhaseResolver mapping
 *
 * Shape matches your fixture DSL:
 * { type, phase, payload }
 *
 * NOTE:
 * If your fixture compiler maps {type, phase, payload} -> {actionTypeName, actionPhaseName, actionPayload},
 * keep this file as the single source of truth.
 */

type FixtureAction<TPayload extends Record<string, unknown>> = {
  type: string;
  phase: ActionPhaseName;
  payload: TPayload;
};

function a<TPayload extends Record<string, unknown>>(
  type: string,
  phase: ActionPhaseName,
  payload: TPayload
): FixtureAction<TPayload> {
  return { type, phase, payload };
}

export const fx = {
  // ---------------------------------------------------------------------------
  // Spin boundary
  // ---------------------------------------------------------------------------

  spinBoundaryStart: (spinBoundaryIdentifier: string, spinModeIdentifier?: string) =>
    a(
      CanonicalActionTypeNames.spinBoundaryStart,
      "frame_prepare",
      {
        spinBoundaryIdentifier,
        ...(spinModeIdentifier ? { spinModeIdentifier } : {}),
      }
    ),

  spinBoundaryEnd: (spinBoundaryIdentifier: string, spinWinAmount?: string) =>
    a(
      CanonicalActionTypeNames.spinBoundaryEnd,
      "settlement",
      {
        spinBoundaryIdentifier,
        ...(spinWinAmount ? { spinWinAmount } : {}),
      }
    ),

  // ---------------------------------------------------------------------------
  // Grid win + removal
  // ---------------------------------------------------------------------------

  gridWinHighlight: (input: {
    positionMask: string;
    winAmount: string;
    symbolIdentifier?: string;
    symbolCount?: string;
    /**
     * Optional MEMBER paylines of a win GROUP (same base symbol + crossing). When present,
     * positionMask = union of all member cells, winAmount = group sum; each member draws one
     * payline (shared sub-segments deduped). Omit for a plain single-line win. Per-line
     * baseWinAmount (win before multiplier) + multiplierApplied ("1" = none) are carried for
     * the highlight presentation.
     */
    lines?: Array<{
      positionMask: string;
      winAmount?: string;
      symbolCount?: string;
      baseWinAmount?: string;
      multiplierApplied?: string;
    }>;
  }) =>
    a(
      CanonicalActionTypeNames.gridWinHighlight,
      "win", // own phase: kept by speed policies that skip the other pre_removal flourishes
      input
    ),

  removeSymbolsByMask: (positionMask: string) =>
    a(
      CanonicalActionTypeNames.removeSymbolsByMask,
      "removal",
      { positionMask }
    ),

  // ---------------------------------------------------------------------------
  // Win meter totals
  // ---------------------------------------------------------------------------

  updateTotalWinMeter: (winAmount: string) =>
    a(
      CanonicalActionTypeNames.updateTotalWinMeter,
      "settlement",
      { winAmount }
    ),

  // ---------------------------------------------------------------------------
  // Needle + pop
  // ---------------------------------------------------------------------------

  needleAward: (input: {
    positionIndex: string;
    winAmount: string;
    highlightMask?: string;
  }) =>
    a(
      CanonicalActionTypeNames.needleAward,
      "pre_removal",
      input
    ),

  symbolPopValue: (value: string) =>
    a(
      CanonicalActionTypeNames.symbolPopValue,
      "pre_removal",
      { value }
    ),

  // ---------------------------------------------------------------------------
  // Multipliers (grid + feature)
  // ---------------------------------------------------------------------------

  presentVisibleMultipliers: (positionMask: string, values: string[]) =>
    a(
      CanonicalActionTypeNames.presentVisibleMultipliers,
      "frame_prepare",
      { positionMask, values }
    ),

  collectGridMultipliers: (positionMask: string, values: string[], collectedSum: string) =>
    a(
      CanonicalActionTypeNames.collectGridMultipliers,
      "post_removal",
      { positionMask, values, collectedSum }
    ),

  addFeatureMultiplier: (input: {
    multiplierAdded: string;
    totalMultiplier: string;
    positionIndex?: string;
  }) =>
    a(
      CanonicalActionTypeNames.addFeatureMultiplier,
      "post_removal",
      input
    ),
  
  collectFeatureMultiplier: (input: {
    multiplierAdded: string;
    collectedSum: string;
  }) =>
    a(
      CanonicalActionTypeNames.collectFeatureMultiplier,
      "post_removal",
      input
    ),

  presentSpinWinMultiplication: (input: {
    baseWinAmount: string;
    multiplierApplied: string;
    finalWinAmount: string;
  }) =>
    a(
      CanonicalActionTypeNames.presentSpinWinMultiplication,
      "settlement",
      input
    ),

  // NOTE: game-defined actions (`apply_win_multiplier`, `detonate_chance_symbol`,
  // `expanding_wild`, ...) have NO helper here — the owning game authors them as plain
  // `{ type, phase, payload }` literals (phase "win" when they must always present).

  // ---------------------------------------------------------------------------
  // Scatter + feature trigger
  // ---------------------------------------------------------------------------

  presentScatterState: (input: {
    /** indices as string[] (the engine parser supports multiple formats; this is simplest) */
    positionMask: string;
    /** Required, as the engine's parser requires it (until 2026-09-26 optional here; review finding 59). */
    scatterCount: string;
    scatterSymbolIdentifier?: string;
  }) =>
    a(
      CanonicalActionTypeNames.presentScatterState,
      "post_removal",
      input
    ),

  // (The `expanding_wild` fixture helper moved GAME-SIDE — it is a game-defined action
  // now, not a canonical one. See the game's sim/fixtures/dsl/expandingWildAction.ts.)

  bonusFeatureWon: (featureIdentifier: string, featureCountAwarded: string) =>
    a(
      CanonicalActionTypeNames.bonusFeatureWon,
      "post_removal",
      { featureIdentifier, featureCountAwarded }
    ),

  // ---------------------------------------------------------------------------
  // Big win overlay
  // ---------------------------------------------------------------------------

  presentBigWin: (input: {
    levelIdentifier: string;
    spinWinAmount: string;
    betAmount: string;
    winRatioLabel: string;
  }) =>
    a(
      CanonicalActionTypeNames.presentBigWin,
      "settlement",
      input
    ),
} as const;